// A piece's free size, angle and span: the menu's typed values, the handles'
// steps, and the turned box a frameless piece is drawn and hit as.

type Point = { x: number; y: number };
type Box = { width: number; height: number };
type Piece = { x: number; y: number; size?: number; rotation?: number; span?: number | null };
type Corner = [number, number];

// MapToken::SIZES.
const MIN_SIZE = 0.25;
const MAX_SIZE = 30;
const SIZE_STEP = 0.25;
const ANGLE_STEP = 15;
// Screen pixels: the handles' frame outside the box, and the shorter side a
// piece needs on a touch screen before they draw.
export const HANDLE_PAD = 8;
export const ANCHOR_RADIUS = 10;
const TOUCH_MIN_SIDE = 56;
const DECIMAL = /^[+-]?(\d+([.,]\d*)?|[.,]\d+)$/;

// "0,5" and "0.5" alike; anything else is NaN.
export function parseDecimal(text: string): number {
	const trimmed = String(text ?? "").trim();
	return DECIMAL.test(trimmed) ? Number(trimmed.replace(",", ".")) : Number.NaN;
}

// The model checks a size as is.
export function validSize(size: number): boolean {
	return Number.isFinite(size) && size >= MIN_SIZE && size <= MAX_SIZE;
}

type Shape = { size?: number; span?: number | null };

// A span of 1 is square, written as none.
const spanOf = (length: number, width: number) => {
	const span = length / width;
	return Math.abs(span - 1) < 1e-9 ? null : span;
};

// The cells along a piece's length, to the millionth as the model rounds it.
export function lengthOf(piece: Shape): number {
	return Math.round((piece.size ?? 1) * (piece.span ?? 1) * 1e6) / 1e6;
}

// The size and span a typed width gives a piece, its length kept; null when
// either side would leave the model's range.
export function withWidth(piece: Shape, width: number): { size: number; span: number | null } | null {
	const length = lengthOf(piece);
	if (!validSize(width) || !validLength(length)) return null;
	return { size: width, span: spanOf(length, width) };
}

// The span a typed length gives a piece, its width kept; null when the
// length is not a number or leaves the range.
export function withLength(piece: Shape, length: number): { span: number | null } | null {
	const width = piece.size ?? 1;
	return validLength(length) ? { span: spanOf(length, width) } : null;
}

// Rounded as MapToken#span_fits rounds the product, so 0.3 × 100 is 30.
function validLength(cells: number): boolean {
	const rounded = Math.round(cells * 1e6) / 1e6;
	return Number.isFinite(cells) && rounded >= MIN_SIZE && rounded <= MAX_SIZE;
}

const stepOf = (snap: boolean) => (snap ? SIZE_STEP : 0.01);

export function stepSize(size: number, snap: boolean, min = MIN_SIZE, max = MAX_SIZE): number {
	const step = stepOf(snap);
	const stepped = Number((Math.round(size / step) * step).toFixed(2));
	return Math.min(Math.max(stepped, min), max);
}

export function stepAngle(degrees: number, snap: boolean): number {
	const step = snap ? ANGLE_STEP : 1;
	return (((Math.round(degrees / step) * step) % 360) + 360) % 360;
}

// Shift held during a drag inverts the piece's own snap for that drag.
export function snapping(token: { snap?: boolean }, shiftKey: boolean): boolean {
	return (token.snap !== false) !== shiftKey;
}

// Degrees clockwise from up, as `rotation` counts them.
export function angleFrom(centre: Point, point: Point): number {
	const degrees = (Math.atan2(point.x - centre.x, centre.y - point.y) * 180) / Math.PI;
	return (degrees + 360) % 360;
}

// A point of the piece's own frame, turned with it, in image pixels.
export function turnPoint(centre: Point, local: Point, rotation: number): Point {
	const [cos, sin] = cosSin(rotation);
	return { x: centre.x + local.x * cos - local.y * sin, y: centre.y + local.x * sin + local.y * cos };
}

function toLocal(centre: Point, point: Point, rotation: number): Point {
	const [cos, sin] = cosSin(rotation);
	const dx = point.x - centre.x;
	const dy = point.y - centre.y;
	return { x: dx * cos + dy * sin, y: -dx * sin + dy * cos };
}

// A disc piece is its square; a frameless one its art fitted whole in `size`
// cells, stretched along its length by `span`. The extents are the turned
// box's axis-aligned half-sizes.
export function pieceBox(token: { size?: number; rotation?: number; span?: number | null }, cellSide: number, art: Box | null) {
	const side = (token.size ?? 1) * cellSide;
	if (!art?.width || !art?.height) return { width: side, height: side, extentX: side / 2, extentY: side / 2 };
	const scale = side / Math.max(art.width, art.height);
	const box = { width: art.width * scale, height: art.height * scale * (token.span ?? 1) };
	const extents = turnedExtents(box, token.rotation ?? 0);
	return { ...box, extentX: extents.x, extentY: extents.y };
}

// A box's axis-aligned half-sizes once turned.
export function turnedExtents({ width, height }: Box, rotation: number): Point {
	const [cos, sin] = cosSin(rotation).map(Math.abs);
	return { x: (width * cos + height * sin) / 2, y: (width * sin + height * cos) / 2 };
}

// A box thinner than `minThickness` is hit as if it were that thick.
export function inBox(box: Box, rotation: number, centre: Point, point: Point, minThickness: number): boolean {
	const local = toLocal(centre, point, rotation);
	return Math.abs(local.x) <= Math.max(box.width, minThickness) / 2 && Math.abs(local.y) <= Math.max(box.height, minThickness) / 2;
}

export function handleFrame(box: Box, zoom: number): Box {
	const pad = (2 * HANDLE_PAD) / zoom;
	return { width: box.width + pad, height: box.height + pad };
}

export function showsHandles(box: Box, zoom: number, coarse: boolean): boolean {
	return !coarse || Math.min(box.width, box.height) * zoom >= TOUCH_MIN_SIDE;
}

// The size a corner drag gives, stepped, and the centre that keeps the
// opposite corner where it was. `unit` is the box at size 1; `pad` what the
// anchor sits outside the corner, in image pixels.
export function resizeFromCorner(token: Piece, [sx, sy]: Corner, point: Point, unit: Box, snap: boolean, pad = 0) {
	const size = token.size ?? 1;
	const rotation = token.rotation ?? 0;
	const span = token.span ?? 1;
	const opposite = turnPoint(token, { x: (-sx * size * unit.width) / 2, y: (-sy * size * unit.height) / 2 }, rotation);
	const local = toLocal(opposite, point, rotation);
	const along = (local.x - sx * pad) * sx * unit.width + (local.y - sy * pad) * sy * unit.height;
	// The limits the span sets, rounded inward to the active step.
	const step = stepOf(snap);
	const min = Number((Math.ceil(Math.max(MIN_SIZE, MIN_SIZE / span) / step - 1e-9) * step).toFixed(2));
	const max = Number((Math.floor(Math.min(MAX_SIZE, MAX_SIZE / span) / step + 1e-9) * step).toFixed(2));
	const next = stepSize(along / (unit.width ** 2 + unit.height ** 2), snap, min, max);
	const centre = turnPoint(opposite, { x: (sx * next * unit.width) / 2, y: (sy * next * unit.height) / 2 }, rotation);
	return { size: next, x: centre.x, y: centre.y };
}

// The span an edge drag gives, its length stepped like a size and divided
// unrounded, so size × span comes back to that length; and the centre that
// keeps the opposite edge where it was. `cell` is the box's height per cell
// of length; a span of 1 is written as none.
export function resizeFromEdge(token: Piece, sy: 1 | -1, point: Point, cell: number, snap: boolean, pad = 0) {
	const size = token.size ?? 1;
	const rotation = token.rotation ?? 0;
	const height = size * (token.span ?? 1) * cell;
	const opposite = turnPoint(token, { x: 0, y: (-sy * height) / 2 }, rotation);
	const length = stepSize((toLocal(opposite, point, rotation).y * sy - pad) / cell, snap);
	const span = length / size;
	const centre = turnPoint(opposite, { x: 0, y: (sy * size * span * cell) / 2 }, rotation);
	return { span: span === 1 ? null : span, x: centre.x, y: centre.y };
}

function cosSin(degrees: number): [number, number] {
	const radians = (degrees * Math.PI) / 180;
	return [Math.cos(radians), Math.sin(radians)];
}
